import React, { useRef, useState, useEffect, useCallback } from 'react';
import { processError } from '../../../services/ServiceUtils';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import { initializeModal } from '../../../utils/ModalUtils';
import { exportStrategy, evaluateStrategy, applyEvaluation, getStrategy, getSnapshots, saveSnapshot, restoreSnapshot, deleteSnapshot } from '../../../services/StrategiesService';
import { getAiProviders } from '../../../services/AiProvidersService';
import { confirmDialog } from '../../../utils/ConfirmDialog';

const DEFAULT = { id: null, name: '', symbol: '' };
const IMPACT_COLOR = { high: 'danger', medium: 'warning', low: 'secondary' };
const IMPACT_LABEL = { high: 'Alto impacto', medium: 'Médio impacto', low: 'Baixo impacto' };
const FIELD_LABEL = { conditions: 'Condições', schedule: 'Agendamento', interval: 'Intervalo', indexes: 'Indicadores' };

function isConditionsInvalid(s) {
    if (s.field !== 'conditions' || s.suggestedValue == null) return false;
    return typeof s.suggestedValue !== 'string' || !s.suggestedValue.includes('MEMORY[');
}

const SUGGESTED_PROMPT = `Você é um especialista em estratégias de trading algorítmico de criptomoedas.

Analise a estratégia exportada abaixo e responda em três seções:

1. **Validade operacional** — essa estratégia está corretamente configurada para executar ordens de compra e venda visando lucro? Há condições que nunca disparariam, lógica contraditória entre compra e venda, ou ausência de proteção de capital (stop loss / take profit)?

2. **Inconsistências** — identifique qualquer conflito interno: condições de entrada que anulam a saída, automações que competem entre si, parâmetros incompatíveis com o símbolo ou timeframe configurado.

3. **Sugestões de melhoria** — o que pode ser ajustado para maximizar lucro e minimizar perdas? Considere os valores dos indicadores, a janela de ordens recentes e os logs das automações como evidência do comportamento real.

Para cada sugestão, indique: o que mudar, o valor atual, o valor sugerido e o motivo.`;

/**
 * props:
 * - data
 */
function ExportStrategyModal(props) {
    const [strategy, setStrategy] = useState(DEFAULT);
    const [tab, setTab] = useState('export');

    // Export tab state
    const [format, setFormat] = useState('markdown');
    const [orderLimit, setOrderLimit] = useState(50);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState('');
    const [promptCopied, setPromptCopied] = useState(false);

    // Evaluate tab state
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [evaluating, setEvaluating] = useState(false);
    const [evaluation, setEvaluation] = useState(null);
    const [evalError, setEvalError] = useState('');
    const [selectedSuggestions, setSelectedSuggestions] = useState({});
    const [applying, setApplying] = useState(false);
    const [applyResult, setApplyResult] = useState(null);
    const [applyMode, setApplyMode] = useState('direct');
    const [warnings, setWarnings] = useState([]);
    // Default ENXUTO: o digest de performance substitui as ordens cruas (mesma filosofia do
    // semanal — evita estourar tokens na auto *USDT). Ordens/logs viram mergulho opcional.
    const [includeOrders, setIncludeOrders] = useState(false);
    const [evalOrderLimit, setEvalOrderLimit] = useState(50);
    const [includeLogs, setIncludeLogs] = useState(false);
    const [includeIndicators, setIncludeIndicators] = useState(true);
    const [includeValidation, setIncludeValidation] = useState(true);
    const [includeDigest, setIncludeDigest] = useState(true);
    const [extraContext, setExtraContext] = useState('');
    const [configExpanded, setConfigExpanded] = useState(true);
    const [evalSubTab, setEvalSubTab] = useState('suggestions');

    // Snapshots tab state
    const [snapshots, setSnapshots] = useState([]);
    const [snapshotLabel, setSnapshotLabel] = useState('');
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [restoring, setRestoring] = useState(null);
    const [restoreResult, setRestoreResult] = useState(null);
    const [snapshotError, setSnapshotError] = useState('');

    const btnClose = useRef('');
    const evalBodyRef = useRef(null);
    const applyResultRef = useRef(null);

    useEffect(() => {
        setStrategy(props.data && props.data.id ? props.data : { ...DEFAULT });
        setEvaluation(null);
        setApplyResult(null);
        setExportError('');
        setEvalError('');
        setSelectedSuggestions({});
    }, [props.data]);

    useEffect(() => {
        initializeModal('modalExportStrategy', () => {
            setTab('export');
            setEvaluation(null);
            setApplyResult(null);
            setFormat('markdown');
            setOrderLimit(50);
            setExportError('');
            setEvalError('');
            setSelectedSuggestions({});
            setWarnings([]);
            setIncludeOrders(true);
            setEvalOrderLimit(50);
            setIncludeLogs(true);
            setExtraContext('');
            setSnapshots([]);
            setSnapshotLabel('');
            setRestoreResult(null);
            setSnapshotError('');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProviders = useCallback(async () => {
        try {
            const list = await getAiProviders();
            setProviders(list || []);
            if (list && list.length) setSelectedProvider(String(list[0].id));
        } catch (_) {}
    }, []);

    const computeWarnings = useCallback(async (strategyId, rowData) => {
        const warns = [];
        if (!rowData.isActive) warns.push({ level: 'info', text: 'Estratégia parada. A análise refletirá a configuração atual, mas sem histórico de execução recente.' });
        if (rowData.monitor && !rowData.monitor.isActive) warns.push({ level: 'warning', text: 'Monitor parado. Sem dados de mercado sendo coletados.' });
        if (rowData.buyAutomation && !rowData.buyAutomation.isActive) warns.push({ level: 'warning', text: `Automação de compra "${rowData.buyAutomation.name}" está parada.` });
        if (rowData.sellAutomation && rowData.sellAutomation.id !== rowData.buyAutomation?.id && !rowData.sellAutomation.isActive)
            warns.push({ level: 'warning', text: `Automação de venda "${rowData.sellAutomation.name}" está parada.` });
        try {
            const full = await getStrategy(strategyId);
            const allAutos = [full.buyAutomation, full.sellAutomation, ...(full.supportingAutomations || [])].filter(Boolean);
            const noLog = allAutos.filter(a => !a.logs);
            if (noLog.length > 0)
                warns.push({ level: 'info', text: `${noLog.length === allAutos.length ? 'Nenhuma' : `${noLog.length} de ${allAutos.length}`} ${noLog.length > 1 ? 'automações' : 'automação'} sem log ativado: ${noLog.map(a => a.name).join(', ')}. Logs ausentes limitam a qualidade da análise de comportamento.` });
        } catch (_) {}
        setWarnings(warns);
    }, []);

    useEffect(() => {
        if (applyResult && applyResultRef.current)
            applyResultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [applyResult]);

    useEffect(() => {
        if (evalError && evalBodyRef.current)
            evalBodyRef.current.scrollTop = 0;
    }, [evalError]);

    const loadSnapshots = useCallback(async (id) => {
        if (!id) return;
        try {
            const list = await getSnapshots(id);
            setSnapshots(list || []);
        } catch (_) {}
    }, []);

    useEffect(() => {
        if (tab === 'evaluate') {
            if (providers.length === 0) loadProviders();
            if (strategy.id) computeWarnings(strategy.id, strategy);
        }
        if (tab === 'snapshots' && strategy.id) loadSnapshots(strategy.id);
    }, [tab, strategy, providers.length, loadProviders, computeWarnings, loadSnapshots]);

    async function onSaveSnapshot() {
        setSnapshotLoading(true);
        setSnapshotError('');
        try {
            await saveSnapshot(strategy.id, snapshotLabel);
            setSnapshotLabel('');
            await loadSnapshots(strategy.id);
        } catch (_) {
            setSnapshotError('Não foi possível salvar a versão.');
        } finally {
            setSnapshotLoading(false);
        }
    }

    async function onRestoreSnapshot(snapshotId) {
        const ok = await confirmDialog({
            title: 'Restaurar versão',
            message: 'Restaurar esta versão? As condições e schedules das automações serão substituídas.',
            confirmLabel: 'Restaurar'
        });
        if (!ok) return;
        setRestoring(snapshotId);
        setRestoreResult(null);
        try {
            const result = await restoreSnapshot(strategy.id, snapshotId);
            setRestoreResult(result);
        } catch (_) {
            setSnapshotError('Não foi possível restaurar a versão.');
        } finally {
            setRestoring(null);
        }
    }

    async function onDeleteSnapshot(snapshotId) {
        const ok = await confirmDialog({
            title: 'Excluir versão',
            message: 'Excluir esta versão?',
            confirmLabel: 'Excluir',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        try {
            await deleteSnapshot(strategy.id, snapshotId);
            await loadSnapshots(strategy.id);
        } catch (_) {}
    }

    async function onCopyPrompt() {
        await navigator.clipboard.writeText(SUGGESTED_PROMPT);
        setPromptCopied(true);
        setTimeout(() => setPromptCopied(false), 2000);
    }

    async function onExportClick() {
        setExportLoading(true);
        setExportError('');
        try {
            const response = await exportStrategy(strategy.id, format, orderLimit);
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="(.+)"/);
            const filename = match ? match[1] : `strategy-${strategy.id}.${format === 'markdown' ? 'md' : 'json'}`;
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url; a.download = filename; a.click();
            window.URL.revokeObjectURL(url);
            btnClose.current.click();
        } catch (err) {
            setExportError(processError(err));
        } finally {
            setExportLoading(false);
        }
    }

    async function onEvaluateClick() {
        setEvaluating(true);
        setEvalError('');
        setEvaluation(null);
        setApplyResult(null);
        setSelectedSuggestions({});
        try {
            const result = await evaluateStrategy(strategy.id, parseInt(selectedProvider), { includeOrders, includeLogs, includeIndicators, includeValidation, includeDigest, orderLimit: evalOrderLimit, extraContext: extraContext.trim() || undefined });
            setEvaluation(result);
            setConfigExpanded(false);
            setEvalSubTab('suggestions');
            const initial = {};
            (result.suggestions || []).forEach(s => { initial[s.id] = !isConditionsInvalid(s); });
            setSelectedSuggestions(initial);
        } catch (err) {
            setEvalError(processError(err));
        } finally {
            setEvaluating(false);
        }
    }

    async function onApplyClick() {
        if (!evaluation) return;
        const toApply = (evaluation.suggestions || [])
            .filter(s => selectedSuggestions[s.id] && s.field !== 'description_only' && s.suggestedValue != null && !isConditionsInvalid(s));
        if (!toApply.length) return;
        setApplying(true);
        setApplyResult(null);
        try {
            const res = await applyEvaluation(strategy.id, toApply.map(s => ({
                id: s.id, targetType: s.targetType, targetId: s.targetId, field: s.field, suggestedValue: s.suggestedValue
            })), applyMode);
            setApplyResult(res);
            if (res.mode === 'copy' && props.onSuccess) {
                setTimeout(() => {
                    btnClose.current.click();
                    props.onSuccess();
                }, 1800);
            }
        } catch (err) {
            setEvalError(processError(err));
        } finally {
            setApplying(false);
        }
    }

    const applicableSelected = evaluation
        ? (evaluation.suggestions || []).filter(s => selectedSuggestions[s.id] && s.field !== 'description_only' && s.suggestedValue != null && !isConditionsInvalid(s)).length
        : 0;

    return (
        <div className="modal fade" id="modalExportStrategy" tabIndex="-1" role="dialog" aria-labelledby="modalExportStrategyTitle" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header pb-0 border-0">
                        <div className="w-100">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <p className="modal-title fw-semibold" id="modalExportStrategyTitle">
                                    {strategy.name || 'Estratégia'} — {strategy.symbol || ''}
                                </p>
                                <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
                            </div>
                            <ul className="nav nav-tabs border-bottom">
                                {[['export', 'Exportar'], ['evaluate', 'Analisar com IA'], ['snapshots', 'Versões']].map(([key, label]) => (
                                    <li key={key} className="nav-item">
                                        <button
                                            className={`nav-link ${tab === key ? 'active' : ''}`}
                                            onClick={() => setTab(key)}
                                        >{label}</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* ── ABA EXPORTAR ── */}
                    {tab === 'export' && (
                        <>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Formato</label>
                                    <div className="d-flex gap-3">
                                        {['markdown', 'json'].map(f => (
                                            <div key={f} className="form-check">
                                                <input className="form-check-input" type="radio" id={`format-${f}`} name="exportFormat" value={f} checked={format === f} onChange={() => setFormat(f)} />
                                                <label className="form-check-label" htmlFor={`format-${f}`}>
                                                    {f === 'markdown' ? 'Markdown (.md)' : 'JSON (.json)'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <small className="text-muted">
                                        {format === 'markdown' ? 'Legível — ideal para colar direto no Claude ou ChatGPT.' : 'Estruturado — ideal para análise programática.'}
                                    </small>
                                </div>
                                <div className="mb-1">
                                    <label className="form-label fw-semibold">Janela de ordens</label>
                                    <input type="number" className="form-control" min={1} max={500} value={orderLimit}
                                        onChange={e => setOrderLimit(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))} />
                                    <small className="text-muted">Número de ordens recentes a incluir (máx. 500).</small>
                                </div>
                                {format === 'markdown' && (
                                    <div className="mb-1">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <label className="form-label fw-semibold mb-0">Prompt sugerido</label>
                                            <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={onCopyPrompt}>
                                                {promptCopied ? '✓ Copiado' : 'Copiar'}
                                            </button>
                                        </div>
                                        <textarea className="form-control form-control-sm text-muted" rows={5} readOnly value={SUGGESTED_PROMPT} style={{ fontSize: '0.75rem', resize: 'none' }} />
                                        <small className="text-muted">Cole esse prompt junto com o arquivo .md no Claude ou ChatGPT.</small>
                                        <small className="text-muted d-block mt-1">Modelo recomendado: <strong>Claude Sonnet</strong> com thinking ativado (medium) — ou <strong>GPT-4o</strong> / <strong>Gemini 2.5 Pro</strong>.</small>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <ActionMessage error={exportError} extraClassName="mt-1 col-7 py-1" />
                                <button type="button" className="btn btn-sm btn-primary" onClick={onExportClick} disabled={exportLoading}>
                                    {exportLoading
                                        ? <span className="spinner-border spinner-border-sm me-1" />
                                        : <svg className="icon icon-xs me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>}
                                    {exportLoading ? 'Gerando...' : 'Baixar'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* ── ABA ANALISAR COM IA ── */}
                    {tab === 'evaluate' && (
                        <>
                            <div ref={evalBodyRef} className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                {/* Config — expandida antes de analisar, colapsada depois */}
                                {configExpanded ? (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Provider de IA</label>
                                            {providers.length === 0
                                                ? <p className="text-muted small">Nenhum provider configurado. Acesse Configurações → IA.</p>
                                                : (
                                                    <select className="form-select" value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)}>
                                                        {providers.map(p => (
                                                            <option key={p.id} value={String(p.id)}>
                                                                {p.provider}{p.model ? ` — ${p.model}` : ''}{p.role === 'primary' ? ' (primário)' : ''}{!p.isActive ? ' (inativo)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            <small className="text-muted">Inclui providers inativos — útil para usar um modelo pago reservado.</small>
                                        </div>

                                        {warnings.length > 0 && (
                                            <div className="mb-3">
                                                {warnings.map((w, i) => (
                                                    <div key={i} className={`alert alert-${w.level === 'warning' ? 'warning' : 'secondary'} py-2 small mb-1`}>
                                                        {w.level === 'warning' ? '⚠ ' : 'ℹ '}{w.text}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mb-3 p-2 border rounded">
                                            <p className="mb-2 small fw-semibold text-muted">Conteúdo a enviar para a IA</p>
                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <div className="form-check mb-0">
                                                    <input className="form-check-input" type="checkbox" id="inclOrders" checked={includeOrders} onChange={e => setIncludeOrders(e.target.checked)} />
                                                    <label className="form-check-label small" htmlFor="inclOrders">Ordens</label>
                                                </div>
                                                {includeOrders && (
                                                    <div className="d-flex align-items-center gap-1">
                                                        <label className="small text-muted mb-0">Qtd:</label>
                                                        <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min={1} max={200} value={evalOrderLimit}
                                                            onChange={e => setEvalOrderLimit(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))} />
                                                    </div>
                                                )}
                                                <div className="form-check mb-0">
                                                    <input className="form-check-input" type="checkbox" id="inclLogs" checked={includeLogs} onChange={e => setIncludeLogs(e.target.checked)} />
                                                    <label className="form-check-label small" htmlFor="inclLogs">Logs das automações</label>
                                                </div>
                                                <div className="form-check mb-0">
                                                    <input className="form-check-input" type="checkbox" id="inclIndicators" checked={includeIndicators} onChange={e => setIncludeIndicators(e.target.checked)} />
                                                    <label className="form-check-label small" htmlFor="inclIndicators">Indicadores ao vivo</label>
                                                </div>
                                                <div className="form-check mb-0">
                                                    <input className="form-check-input" type="checkbox" id="inclValidation" checked={includeValidation} onChange={e => setIncludeValidation(e.target.checked)} />
                                                    <label className="form-check-label small" htmlFor="inclValidation">Validação offline</label>
                                                </div>
                                                <div className="form-check mb-0">
                                                    <input className="form-check-input" type="checkbox" id="inclDigest" checked={includeDigest} onChange={e => setIncludeDigest(e.target.checked)} />
                                                    <label className="form-check-label small" htmlFor="inclDigest">Resumo de performance</label>
                                                </div>
                                            </div>
                                            {includeIndicators && (
                                                <small className="text-muted d-block mt-1">Valores atuais do Redis (RSI, EMA, regime, preço) — ajuda a IA a calibrar thresholds com o mercado real.</small>
                                            )}
                                            {includeValidation && (
                                                <small className="text-muted d-block mt-1">Resultado da bateria de backtest multi-cenário (grade/comutador por regime) — fundamenta as sugestões em evidência offline.</small>
                                            )}
                                            {includeDigest && (
                                                <small className="text-muted d-block mt-1">Resumo compacto de performance (trades FILLED + lucro realizado FIFO) — mesma evidência da reavaliação semanal, sem estourar tokens.</small>
                                            )}
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label small fw-semibold mb-1">Contexto adicional <span className="text-muted fw-normal">(opcional)</span></label>
                                            <textarea
                                                className="form-control form-control-sm"
                                                rows={3}
                                                placeholder="Ex: estamos em mercado de baixa, priorize proteção de capital; foque na automação de venda..."
                                                value={extraContext}
                                                onChange={e => setExtraContext(e.target.value)}
                                            />
                                            <small className="text-muted">Orienta a análise sem alterar o formato de resposta.</small>
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex align-items-center gap-2 mb-3 p-2 border rounded bg-light small text-muted">
                                        <span className="fw-semibold text-dark">
                                            {providers.find(p => String(p.id) === selectedProvider)?.provider || '—'}
                                        </span>
                                        <span>·</span>
                                        <span>{includeOrders ? `${evalOrderLimit} ordens` : 'sem ordens'}</span>
                                        <span>·</span>
                                        <span>{includeLogs ? 'com logs' : 'sem logs'}</span>
                                        {includeIndicators && <><span>·</span><span>indicadores ao vivo</span></>}
                                        {includeValidation && <><span>·</span><span>validação offline</span></>}
                                        {includeDigest && <><span>·</span><span>resumo de performance</span></>}
                                        {extraContext && <><span>·</span><span>contexto personalizado</span></>}
                                        <button type="button" className="btn btn-link btn-sm py-0 ms-auto text-muted" onClick={() => setConfigExpanded(true)}>
                                            Reconfigurar
                                        </button>
                                    </div>
                                )}

                                {evalError && <div className="alert alert-danger py-2 small">{evalError}</div>}

                                {/* Resultado da avaliação */}
                                {evaluation && !evaluating && (
                                    <>
                                        {/* Score + resumo — sempre visível */}
                                        <div className="d-flex align-items-start gap-3 mb-3 p-3 border rounded bg-light">
                                            <span className={`badge bg-${evaluation.score >= 7 ? 'success' : evaluation.score >= 4 ? 'warning' : 'danger'} fs-6 flex-shrink-0`}>
                                                {evaluation.score}/10
                                            </span>
                                            <p className="mb-0 small">{evaluation.summary}</p>
                                        </div>

                                        {/* Sub-abas: Sugestões | Aplicar */}
                                        {evaluation.suggestions && evaluation.suggestions.length > 0 && (
                                            <>
                                                <ul className="nav nav-tabs mb-3">
                                                    <li className="nav-item">
                                                        <button className={`nav-link ${evalSubTab === 'suggestions' ? 'active' : ''}`} onClick={() => setEvalSubTab('suggestions')}>
                                                            Sugestões ({evaluation.suggestions.length})
                                                        </button>
                                                    </li>
                                                    <li className="nav-item">
                                                        <button className={`nav-link ${evalSubTab === 'apply' ? 'active' : ''} ${applicableSelected === 0 ? 'text-muted' : ''}`} onClick={() => setEvalSubTab('apply')}>
                                                            Aplicar{applicableSelected > 0 ? ` (${applicableSelected})` : ''}
                                                        </button>
                                                    </li>
                                                </ul>

                                                {/* Sub-aba: Sugestões */}
                                                {evalSubTab === 'suggestions' && (
                                                    <>
                                                        {evaluation.suggestions.map(s => {
                                                            const condInvalid = isConditionsInvalid(s);
                                                            return (
                                                                <div key={s.id} className={`card mb-2 border-${IMPACT_COLOR[s.impact] || 'secondary'}`}>
                                                                    <div className="card-body py-2 px-3">
                                                                        <div className="d-flex align-items-start gap-2">
                                                                            {s.field !== 'description_only' && s.suggestedValue != null && !condInvalid && (
                                                                                <input type="checkbox" className="form-check-input mt-1 flex-shrink-0" checked={!!selectedSuggestions[s.id]}
                                                                                    onChange={e => setSelectedSuggestions(prev => ({ ...prev, [s.id]: e.target.checked }))} />
                                                                            )}
                                                                            {condInvalid && (
                                                                                <input type="checkbox" className="form-check-input mt-1 flex-shrink-0" checked={false} disabled title="Não aplicável automaticamente" />
                                                                            )}
                                                                            <div className="flex-grow-1">
                                                                                <div className="d-flex gap-2 align-items-center mb-1 flex-wrap">
                                                                                    <span className={`badge bg-${IMPACT_COLOR[s.impact]}`}>{IMPACT_LABEL[s.impact]}</span>
                                                                                    <span className="badge bg-secondary">{s.targetName}</span>
                                                                                    {s.field === 'description_only' && <span className="badge bg-light text-dark border">Manual</span>}
                                                                                    {condInvalid && <span className="badge bg-warning text-dark">Aplicar manualmente</span>}
                                                                                </div>
                                                                                <p className="mb-1 small fw-semibold">{s.description}</p>
                                                                                <p className="mb-0 text-muted small">{s.reasoning}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                        {applicableSelected > 0 && (
                                                            <div className="text-end mt-2">
                                                                <button className="btn btn-sm btn-outline-primary" onClick={() => setEvalSubTab('apply')}>
                                                                    Ver preview e aplicar ({applicableSelected}) →
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {/* Sub-aba: Aplicar */}
                                                {evalSubTab === 'apply' && (
                                                    applicableSelected === 0
                                                        ? <p className="text-muted small">Nenhuma sugestão selecionada. Volte para a aba Sugestões e marque as que deseja aplicar.</p>
                                                        : (() => {
                                                            const toApply = (evaluation.suggestions || []).filter(s =>
                                                                selectedSuggestions[s.id] && s.field !== 'description_only' && s.suggestedValue != null && !isConditionsInvalid(s)
                                                            );
                                                            return (
                                                                <>
                                                                    <p className="fw-semibold small mb-2">Alterações selecionadas</p>
                                                                    {toApply.map(s => {
                                                                        const conds = s.field === 'conditions'
                                                                            ? s.suggestedValue.split('&&').map(c => c.trim()).filter(Boolean)
                                                                            : s.field === 'indexes'
                                                                                ? s.suggestedValue.split(',').map(c => c.trim()).filter(Boolean)
                                                                                : null;
                                                                        return (
                                                                            <div key={s.id} className="mb-3">
                                                                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                                                                    <span className={`badge bg-${IMPACT_COLOR[s.impact]}`}>{IMPACT_LABEL[s.impact]}</span>
                                                                                    <span className="badge bg-secondary">{s.targetName}</span>
                                                                                    <span className="badge bg-light text-dark border">{FIELD_LABEL[s.field] || s.field}</span>
                                                                                    <button className="btn btn-link btn-sm py-0 ms-auto text-muted" style={{ fontSize: '0.78em' }}
                                                                                        onClick={() => { setSelectedSuggestions(prev => ({ ...prev, [s.id]: false })); }}>
                                                                                        Remover
                                                                                    </button>
                                                                                </div>
                                                                                <p className="mb-1 small text-muted">{s.description}</p>
                                                                                {conds ? (
                                                                                    <div className="d-flex flex-wrap gap-1">
                                                                                        {conds.map((cond, i) => (
                                                                                            <span key={i} className="badge bg-primary" style={{ fontWeight: 400, fontSize: '0.72em', fontFamily: 'monospace' }}>{cond}</span>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="badge bg-success" style={{ fontWeight: 400, fontSize: '0.8em', fontFamily: 'monospace' }}>{s.suggestedValue}</span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    <div className="mt-3 p-3 border rounded">
                                                                        <p className="fw-semibold mb-2 small">Modo de aplicação</p>
                                                                        <div className="d-flex gap-3 mb-2">
                                                                            {[['direct', 'Nesta estratégia', 'Altera diretamente e recarrega o beholder imediatamente.'],
                                                                              ['copy', 'Criar cópia', 'Cria uma cópia com as alterações e desativa a original.']].map(([v, l, hint]) => (
                                                                                <div key={v} className="form-check">
                                                                                    <input className="form-check-input" type="radio" id={`applyMode-${v}`} name="applyMode" value={v} checked={applyMode === v} onChange={() => setApplyMode(v)} />
                                                                                    <label className="form-check-label" htmlFor={`applyMode-${v}`}>
                                                                                        <span className="d-block">{l}</span>
                                                                                        <small className="text-muted">{hint}</small>
                                                                                    </label>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            );
                                                        })()
                                                )}

                                                {evaluation.alternativeStrategy && evalSubTab === 'suggestions' && (
                                                    <div className="alert alert-info mt-3 py-2">
                                                        <strong>Alternativa de estratégia</strong>
                                                        <p className="mb-1 small">{evaluation.alternativeStrategy.description}</p>
                                                        <p className="mb-0 text-muted small">{evaluation.alternativeStrategy.reasoning}</p>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Resultado do apply */}
                                        {applyResult && (
                                            <div ref={applyResultRef} className={`alert alert-${applyResult.mode === 'copy' ? 'success' : 'info'} mt-3 py-2 d-flex align-items-center justify-content-between gap-2`}>
                                                <span>
                                                    {applyResult.mode === 'copy'
                                                        ? `Cópia criada com as alterações. A estratégia original foi desativada.`
                                                        : `Alterações aplicadas. ${(applyResult.results || []).filter(r => r.ok).length} de ${(applyResult.results || []).length} sugestões aplicadas com sucesso.`}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary text-nowrap"
                                                    onClick={() => {
                                                        setApplyResult(null);
                                                        setEvaluation(null);
                                                        setSelectedSuggestions({});
                                                        setConfigExpanded(true);
                                                        setEvalSubTab('suggestions');
                                                    }}
                                                >
                                                    Nova análise
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}

                                {evaluating && (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status" />
                                        <p className="text-muted mt-2 small">Analisando estratégia com IA...</p>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer gap-2">
                                <button type="button" className="btn btn-sm btn-primary" onClick={onEvaluateClick} disabled={evaluating || !selectedProvider}>
                                    {evaluating ? <><span className="spinner-border spinner-border-sm me-1" />Analisando...</> : 'Analisar'}
                                </button>
                                {evalSubTab === 'apply' && applicableSelected > 0 && !applyResult && (
                                    <button type="button" className="btn btn-sm btn-success" onClick={onApplyClick} disabled={applying}>
                                        {applying ? <><span className="spinner-border spinner-border-sm me-1" />Aplicando...</> : `Aplicar ${applicableSelected} sugest${applicableSelected > 1 ? 'ões' : 'ão'}`}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                    {/* ── ABA VERSÕES ── */}
                    {tab === 'snapshots' && (
                        <>
                            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                {/* Salvar versão manual */}
                                <div className="mb-3 p-2 border rounded">
                                    <p className="mb-2 small fw-semibold text-muted">Salvar versão atual</p>
                                    <div className="d-flex gap-2">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            placeholder="Nome da versão (opcional)"
                                            value={snapshotLabel}
                                            onChange={e => setSnapshotLabel(e.target.value)}
                                            maxLength={80}
                                        />
                                        <button className="btn btn-sm btn-outline-primary text-nowrap" onClick={onSaveSnapshot} disabled={snapshotLoading}>
                                            {snapshotLoading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                                        </button>
                                    </div>
                                    <small className="text-muted">Versões automáticas são criadas antes de cada Apply.</small>
                                </div>

                                {snapshotError && <div className="alert alert-danger py-2 small mb-2">{snapshotError}</div>}

                                {restoreResult && (
                                    <div className="alert alert-success py-2 small mb-2">
                                        Versão restaurada. {restoreResult.results.filter(r => r.ok).length}/{restoreResult.results.length} componentes atualizados.
                                    </div>
                                )}

                                {snapshots.length === 0
                                    ? <p className="text-muted small">Nenhuma versão salva ainda.</p>
                                    : (
                                        <table className="table table-sm table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Versão</th>
                                                    <th>Tipo</th>
                                                    <th>Data</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {snapshots.map(s => (
                                                    <tr key={s.id}>
                                                        <td className="small">{s.label}</td>
                                                        <td>
                                                            <span className={`badge bg-${s.source === 'auto' ? 'secondary' : 'info'}`}>
                                                                {s.source === 'auto' ? 'Auto' : 'Manual'}
                                                            </span>
                                                        </td>
                                                        <td className="small text-muted text-nowrap">
                                                            {new Date(s.createdAt).toLocaleString('pt-BR')}
                                                        </td>
                                                        <td className="text-end text-nowrap">
                                                            <button
                                                                className="btn btn-xs btn-sm btn-outline-success me-1"
                                                                onClick={() => onRestoreSnapshot(s.id)}
                                                                disabled={restoring === s.id}
                                                                title="Restaurar esta versão"
                                                            >
                                                                {restoring === s.id ? <span className="spinner-border spinner-border-sm" /> : 'Restaurar'}
                                                            </button>
                                                            <button
                                                                className="btn btn-xs btn-sm btn-outline-danger"
                                                                onClick={() => onDeleteSnapshot(s.id)}
                                                                title="Excluir"
                                                            >✕</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )
                                }
                                <small className="text-muted d-block mt-1">Máximo 20 versões por estratégia — as mais antigas são removidas automaticamente.</small>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-sm btn-secondary" data-bs-dismiss="modal">Fechar</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExportStrategyModal;
